import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import SocialShare from "@/components/SocialShare";
import SEO from "@/components/SEO";
import { ArticleJsonLd } from "@/components/JsonLd";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Eye,
  Heart,
  MessageCircle,
  Send,
  User,
  Reply,
  Shield,
} from "lucide-react";

// Fonction pour calculer le temps de lecture
const calculateReadingTime = (content) => {
  if (!content) return 1;
  const wordsPerMinute = 200; // Moyenne de lecture en français
  const words = content.trim().split(/\s+/).length;
  const minutes = Math.ceil(words / wordsPerMinute);
  return Math.max(1, minutes);
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentLikes, setCommentLikes] = useState({});
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState(null); // {id, userName}
  const [replyContent, setReplyContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  useEffect(() => {
    if (article) {
      fetchComments();
      checkLikeStatus();
    }
  }, [article, token]);

  const fetchArticle = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/articles/${slug}`, { headers });
      
      if (response.ok) {
        const data = await response.json();
        setArticle(data);
        setLikeCount(data.likes_count);
      } else if (response.status === 404) {
        navigate("/blog");
        toast.error("Article non trouvé");
      }
    } catch (error) {
      console.error("Error fetching article:", error);
      toast.error("Erreur lors du chargement de l'article");
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await fetch(`${API}/articles/${article.id}/comments`);
      if (response.ok) {
        const data = await response.json();
        setComments(data);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  const checkLikeStatus = async () => {
    if (!token || !article) return;
    
    try {
      const response = await fetch(`${API}/articles/${article.id}/like-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setIsLiked(data.liked);
      }
    } catch (error) {
      console.error("Error checking like status:", error);
    }
  };

  const handleLike = async () => {
    if (!token) {
      toast.error("Connectez-vous pour aimer cet article");
      navigate("/login");
      return;
    }

    try {
      const response = await fetch(`${API}/articles/${article.id}/like`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setIsLiked(data.liked);
        setLikeCount((prev) => (data.liked ? prev + 1 : prev - 1));
        toast.success(data.liked ? "Article aimé !" : "Like retiré");
      }
    } catch (error) {
      console.error("Error toggling like:", error);
      toast.error("Erreur lors de l'action");
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!token) {
      toast.error("Connectez-vous pour commenter");
      navigate("/login");
      return;
    }

    if (!newComment.trim()) {
      toast.error("Le commentaire ne peut pas être vide");
      return;
    }

    setSubmittingComment(true);

    try {
      const response = await fetch(`${API}/articles/${article.id}/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: newComment }),
      });

      if (response.ok) {
        await fetchComments(); // Refresh to get nested structure
        setNewComment("");
        toast.success("Commentaire ajouté !");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'ajout du commentaire");
      }
    } catch (error) {
      console.error("Error submitting comment:", error);
      toast.error("Erreur lors de l'ajout du commentaire");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleSubmitReply = async (parentId) => {
    if (!token) {
      toast.error("Connectez-vous pour répondre");
      navigate("/login");
      return;
    }

    if (!replyContent.trim()) {
      toast.error("La réponse ne peut pas être vide");
      return;
    }

    setSubmittingComment(true);

    try {
      const response = await fetch(`${API}/articles/${article.id}/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: replyContent, parent_id: parentId }),
      });

      if (response.ok) {
        await fetchComments(); // Refresh to get nested structure
        setReplyContent("");
        setReplyingTo(null);
        toast.success("Réponse ajoutée !");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'ajout de la réponse");
      }
    } catch (error) {
      console.error("Error submitting reply:", error);
      toast.error("Erreur lors de l'ajout de la réponse");
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!article) {
    return null;
  }

  const currentUrl = window.location.href;
  const SITE_URL = process.env.REACT_APP_SITE_URL || "https://micheltsitsok.com";
  const canonicalUrl = `${SITE_URL}/article/${article.slug}`;
  
  // Extract first 160 chars of content for description
  const articleDescription = article.content 
    ? article.content.replace(/[#*_`]/g, '').substring(0, 160) + "..."
    : article.excerpt || `Article par ${article.author_name}`;

  return (
    <>
      <SEO
        title={article.title}
        description={articleDescription}
        image={article.cover_image}
        url={canonicalUrl}
        type="article"
        author={article.author_name}
        publishedTime={article.created_at}
        tags={article.tags || []}
      />
      <ArticleJsonLd
        title={article.title}
        description={articleDescription}
        image={article.cover_image}
        url={canonicalUrl}
        datePublished={article.created_at}
        dateModified={article.updated_at}
        authorName={article.author_name}
        tags={article.tags || []}
      />
      <div className="min-h-screen pt-24 pb-16" data-testid="blog-post-page">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Link to="/">
            <Button variant="ghost" className="gap-2" data-testid="back-to-blog">
              <ArrowLeft className="h-4 w-4" />
              Retour au blog
            </Button>
          </Link>
        </motion.div>

        {/* Cover Image */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-2xl overflow-hidden mb-8 aspect-video"
        >
          <img
            src={article.cover_image}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </motion.div>

        {/* Article Header */}
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs font-medium px-3 py-1 rounded-full bg-primary/10 text-primary"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            {article.title}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-6 text-muted-foreground mb-6">
            <div className="flex items-center gap-2">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {article.author_name?.charAt(0) || "A"}
                </AvatarFallback>
              </Avatar>
              <span className="font-medium text-foreground">{article.author_name}</span>
            </div>
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {formatDate(article.created_at)}
            </span>
            <span className="flex items-center gap-1">
              <Eye className="h-4 w-4" />
              {article.views} vues
            </span>
            <span className="flex items-center gap-1" data-testid="reading-time">
              <Clock className="h-4 w-4" />
              {calculateReadingTime(article.content)} min de lecture
            </span>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center justify-between gap-4 py-4 border-t border-b border-border">
            <div className="flex items-center gap-4">
              <Button
                variant={isLiked ? "default" : "outline"}
                size="sm"
                onClick={handleLike}
                data-testid="like-button"
                className="gap-2"
              >
                <Heart className={`h-4 w-4 ${isLiked ? "fill-current" : ""}`} />
                {likeCount}
              </Button>
              <span className="flex items-center gap-1 text-muted-foreground">
                <MessageCircle className="h-4 w-4" />
                {comments.length} commentaires
              </span>
            </div>
            <SocialShare url={currentUrl} title={article.title} />
          </div>
        </motion.header>

        {/* Article Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="blog-content prose-dark mb-12"
          data-testid="article-content"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {article.content}
          </ReactMarkdown>
        </motion.div>

        {/* Comments Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="border-t border-border pt-8"
          data-testid="comments-section"
        >
          <h2 className="font-heading text-2xl font-bold text-foreground mb-6">
            Commentaires ({comments.length})
          </h2>

          {/* Comment Form */}
          <form onSubmit={handleSubmitComment} className="mb-8">
            <Textarea
              placeholder={
                user
                  ? "Écrivez votre commentaire..."
                  : "Connectez-vous pour commenter"
              }
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              disabled={!user}
              data-testid="comment-input"
              className="mb-4 min-h-[100px]"
            />
            <Button
              type="submit"
              disabled={!user || submittingComment}
              data-testid="submit-comment"
              className="gap-2"
            >
              <Send className="h-4 w-4" />
              {submittingComment ? "Envoi..." : "Publier"}
            </Button>
          </form>

          {/* Comments List */}
          <div className="space-y-6">
            {comments.length > 0 ? (
              comments.map((comment) => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  user={user}
                  token={token}
                  formatDate={formatDate}
                  replyingTo={replyingTo}
                  setReplyingTo={setReplyingTo}
                  replyContent={replyContent}
                  setReplyContent={setReplyContent}
                  handleSubmitReply={handleSubmitReply}
                  submittingComment={submittingComment}
                />
              ))
            ) : (
              <p className="text-center text-muted-foreground py-8">
                Aucun commentaire pour le moment. Soyez le premier à commenter !
              </p>
            )}
          </div>
        </motion.section>
      </article>
    </div>
    </>
  );
};

// Comment Item Component with like, reply functionality and admin badge
const CommentItem = ({ 
  comment, 
  user, 
  token, 
  formatDate, 
  replyingTo, 
  setReplyingTo, 
  replyContent, 
  setReplyContent,
  handleSubmitReply,
  submittingComment,
  isReply = false 
}) => {
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(comment.likes_count || 0);
  const [liking, setLiking] = useState(false);

  useEffect(() => {
    if (user && token) {
      checkLiked();
    }
  }, [user, token]);

  const checkLiked = async () => {
    try {
      const res = await fetch(`${API}/comments/${comment.id}/liked`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLiked(data.liked);
      }
    } catch {}
  };

  const handleLike = async () => {
    if (!user || !token) {
      toast.error("Connectez-vous pour liker");
      return;
    }
    setLiking(true);
    try {
      const res = await fetch(`${API}/comments/${comment.id}/like`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLiked(data.liked);
        setLikesCount(data.likes_count);
      }
    } catch {
      toast.error("Erreur");
    } finally {
      setLiking(false);
    }
  };

  const isAdmin = comment.user_role === "admin";
  const isEditor = comment.user_role === "editor";

  return (
    <div className={isReply ? "" : ""}>
      <div
        data-testid={`comment-${comment.id}`}
        className={`flex gap-4 p-4 rounded-xl bg-card border border-border ${isReply ? "ml-12 mt-3" : ""}`}
      >
        <Avatar className="h-10 w-10 flex-shrink-0">
          <AvatarFallback className={`${isAdmin ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
            {comment.user_name?.charAt(0) || "U"}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="font-medium text-foreground">
              {comment.user_name}
            </span>
            {isAdmin && (
              <Badge variant="default" className="text-xs gap-1 bg-primary">
                <Shield className="h-3 w-3" />
                Admin
              </Badge>
            )}
            {isEditor && (
              <Badge variant="secondary" className="text-xs">
                Éditeur
              </Badge>
            )}
            <span className="text-xs text-muted-foreground">
              {formatDate(comment.created_at)}
            </span>
          </div>
          <p className="text-muted-foreground mb-3">{comment.content}</p>
          <div className="flex items-center gap-4">
            <button
              onClick={handleLike}
              disabled={liking}
              className={`flex items-center gap-1.5 text-sm transition-colors ${
                liked ? "text-red-500" : "text-muted-foreground hover:text-red-500"
              }`}
            >
              <Heart className={`h-4 w-4 ${liked ? "fill-current" : ""}`} />
              <span>{likesCount}</span>
            </button>
            {!isReply && user && (
              <button
                onClick={() => setReplyingTo(replyingTo?.id === comment.id ? null : { id: comment.id, userName: comment.user_name })}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <Reply className="h-4 w-4" />
                <span>Répondre</span>
              </button>
            )}
          </div>

          {/* Reply Form */}
          {replyingTo?.id === comment.id && (
            <div className="mt-4 space-y-3">
              <Textarea
                placeholder={`Répondre à ${replyingTo.userName}...`}
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                className="min-h-[80px]"
                data-testid={`reply-input-${comment.id}`}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => handleSubmitReply(comment.id)}
                  disabled={submittingComment}
                  data-testid={`submit-reply-${comment.id}`}
                >
                  <Send className="h-3 w-3 mr-1" />
                  {submittingComment ? "Envoi..." : "Répondre"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setReplyingTo(null);
                    setReplyContent("");
                  }}
                >
                  Annuler
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="space-y-3">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              user={user}
              token={token}
              formatDate={formatDate}
              replyingTo={replyingTo}
              setReplyingTo={setReplyingTo}
              replyContent={replyContent}
              setReplyContent={setReplyContent}
              handleSubmitReply={handleSubmitReply}
              submittingComment={submittingComment}
              isReply={true}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogPostPage;
